(() => {
  const ROOT_SELECTOR = '[data-awards-root]';
  const DATA_SELECTOR = '[data-awards-data]';
  const VERSION = 'liquid-metaobjects-v2-all-pages';
  const dataPromises = new WeakMap();

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const normaliseSku = (value) => String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]*MAG$/, '')
    .trim();

  const dateLabel = (value) => {
    if (!value) return '';
    const date = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return '';
    const day = date.getUTCDate();
    const month = date.getUTCMonth();
    if (day === 1 && month === 0) return String(date.getUTCFullYear());
    return date.toLocaleDateString('en-AU', {
      day: day === 1 ? undefined : 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const normaliseAward = (award) => ({
    ...award,
    type: String(award.type || '').toLowerCase(),
    score_display: String(award.score_display || '').trim(),
    score_suffix: award.score_suffix || 'points',
    sort_index: award.sort_index === null || award.sort_index === undefined || award.sort_index === ''
      ? null
      : Number.parseInt(award.sort_index, 10)
  });

  const parseAwards = (dataElement) => {
    const parsed = JSON.parse(dataElement.textContent || '[]');
    if (!Array.isArray(parsed)) throw new Error('Liquid awards data is not an array');
    return parsed.map(normaliseAward);
  };

  const findDataElement = (root) => {
    const section = root.closest('.shopify-section--tabs') || root.closest('.shopify-section') || document;
    return section.querySelector(DATA_SELECTOR) || document.querySelector(DATA_SELECTOR);
  };

  const fetchAwardsPage = async (dataElement, page) => {
    const sectionId = String(dataElement.dataset.sectionId || '').trim();
    const pageParam = String(dataElement.dataset.pageParam || 'page').trim() || 'page';
    if (!sectionId) throw new Error('Awards section ID was not found');

    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set(pageParam, String(page));

    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error(`Awards page ${page} returned ${response.status}`);

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const pageData = [...documentFragment.querySelectorAll(DATA_SELECTOR)]
      .find((element) => element.dataset.sectionId === sectionId)
      || documentFragment.querySelector(DATA_SELECTOR);
    if (!pageData) throw new Error(`Awards data was not found on page ${page}`);
    return parseAwards(pageData);
  };

  const loadAllAwards = (dataElement) => {
    if (dataPromises.has(dataElement)) return dataPromises.get(dataElement);

    const promise = (async () => {
      const initialAwards = parseAwards(dataElement);
      const pageCount = Math.max(1, Number.parseInt(dataElement.dataset.pages || '1', 10) || 1);
      const currentPage = Math.max(1, Number.parseInt(dataElement.dataset.currentPage || '1', 10) || 1);
      if (pageCount === 1) return initialAwards;

      const pageNumbers = Array.from({ length: pageCount }, (_, index) => index + 1)
        .filter((page) => page !== currentPage);
      const results = await Promise.allSettled(
        pageNumbers.map((page) => fetchAwardsPage(dataElement, page))
      );
      const awards = [...initialAwards];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          awards.push(...result.value);
        } else {
          console.error(`[awards] Unable to load page ${pageNumbers[index]}:`, result.reason);
        }
      });

      const uniqueAwards = new Map();
      awards.forEach((award) => {
        const key = String(award.id || `${award.master_sku}|${award.vintage_year}|${award.sort_index}|${award.date}|${award.score_display}`);
        if (!uniqueAwards.has(key)) uniqueAwards.set(key, award);
      });
      return [...uniqueAwards.values()];
    })();

    dataPromises.set(dataElement, promise);
    return promise;
  };

  const heading = (award) => {
    if (award.type === 'points') {
      const critic = award.critic_name ? `<span class="wa-critic">${escapeHtml(award.critic_name)}</span>` : '';
      const publication = award.publication ? `<span class="wa-publication">${escapeHtml(award.publication)}</span>` : '';
      return critic || publication
        ? `<div class="wa-heading">${critic}${critic && publication ? '<span class="wa-sep">,&nbsp;</span>' : ''}${publication}</div>`
        : '';
    }
    return award.competition
      ? `<div class="wa-heading"><span class="wa-competition">${escapeHtml(award.competition)}</span></div>`
      : '';
  };

  const card = (award, medalSrc) => {
    const score = award.score_display;
    const size = score.length >= 4 || /[+.]/.test(score)
      ? 'wa-score--xs'
      : score.length === 3
        ? 'wa-score--sm'
        : 'wa-score--lg';
    const media = award.type === 'points' && score
      ? `<div class="wa-score-wrap"><span class="wa-score ${size}">${escapeHtml(score)}</span></div>`
      : medalSrc
        ? `<img class="wa-medal-img" src="${escapeHtml(medalSrc)}" alt="Medal" loading="lazy" style="object-position:center!important;transform:translate(var(--wa-medal-shift-x,0px),var(--wa-medal-shift-y,0px))!important;">`
        : '<span class="wa-medal-icon" aria-hidden="true">&#9733;</span>';
    const topline = award.type === 'points'
      ? `<div class="wa-topline">${escapeHtml(award.score_suffix).toUpperCase()}</div>`
      : award.medal_level
        ? `<div class="wa-topline">${escapeHtml(award.medal_level).toUpperCase()}</div>`
        : '';
    const quote = award.quote_html
      ? `<div class="wa-quote-row" data-expanded="false"><div class="wa-quote">${award.quote_html}</div><button type="button" class="wa-readmore" aria-expanded="false">Read more</button></div>`
      : '';

    return `<article class="wine-award-card"><div class="wa-media">${media}</div><div class="wa-text">${topline}<div class="wa-text-bottom">${heading(award)}${award.date ? `<div class="wa-date">${escapeHtml(dateLabel(award.date))}</div>` : ''}</div></div>${quote}</article>`;
  };

  const sortAwards = (awards) => [...awards].sort((a, b) => {
    const index = (a.sort_index ?? 9999) - (b.sort_index ?? 9999);
    return index || new Date(b.date || 0) - new Date(a.date || 0);
  });

  const groupMarkup = (vintage, awards, medalSrc, open) => {
    const label = vintage === 'NV' ? 'Non-Vintage' : `${escapeHtml(vintage)} Vintage`;
    return `<details class="wine-awards-vintage"${open ? ' open' : ''}><summary class="wine-awards-vintage__summary"><span>${label}</span><span class="wine-awards-vintage__icon" aria-hidden="true"></span></summary><div class="wine-awards-vintage__content"><div class="wine-awards">${sortAwards(awards).map((award) => card(award, medalSrc)).join('')}</div></div></details>`;
  };

  const renderRoot = async (root) => {
    try {
      const dataElement = findDataElement(root);
      if (!dataElement) throw new Error('Liquid awards data element was not found');

      const allAwards = await loadAllAwards(dataElement);
      const currentSku = normaliseSku(root.dataset.masterSku);
      const currentVintage = String(root.dataset.vintageYear || '').trim() || 'NV';
      if (!currentSku) throw new Error('The product master SKU is blank');

      const awards = allAwards.filter((award) => normaliseSku(award.master_sku) === currentSku);
      if (!awards.length) {
        root.innerHTML = '<p style="opacity:.65;font-size:12px">This release is awaiting review.</p>';
        return;
      }

      const groups = new Map();
      awards.forEach((award) => {
        const vintage = String(award.vintage_year || '').trim() || 'NV';
        if (!groups.has(vintage)) groups.set(vintage, []);
        groups.get(vintage).push(award);
      });
      const vintages = [...groups.keys()].sort((a, b) => (Number.parseInt(b, 10) || -1) - (Number.parseInt(a, 10) || -1));
      const openVintage = groups.has(currentVintage) ? currentVintage : vintages[0];
      root.innerHTML = `<div class="wine-awards-history">${vintages.map((vintage) => groupMarkup(vintage, groups.get(vintage), root.dataset.medalSrc || '', vintage === openVintage)).join('')}</div>`;

      if (new URLSearchParams(window.location.search).has('debug_awards')) {
        root.insertAdjacentHTML('afterbegin', `<pre style="white-space:pre-wrap;font-size:11px;padding:.5rem;background:#f5f5f5">version: ${VERSION}\ncurrent SKU: ${escapeHtml(currentSku)}\ncurrent vintage: ${escapeHtml(currentVintage)}\nLiquid awards loaded: ${allAwards.length}\nmatching awards: ${awards.length}</pre>`);
      }

      window.dispatchEvent(new Event('resize'));
    } catch (error) {
      console.error('[awards] Liquid data error:', error);
      const debug = new URLSearchParams(window.location.search).has('debug_awards');
      root.innerHTML = debug
        ? `<pre style="white-space:pre-wrap;font-size:11px;padding:.5rem;background:#f5f5f5">version: ${VERSION}\n${escapeHtml(error.stack || error.message)}</pre>`
        : '<p style="opacity:.65;font-size:12px">Awards unavailable.</p>';
    }
  };

  const initialise = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(renderRoot);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.wa-readmore');
    if (!button) return;
    const row = button.closest('.wa-quote-row');
    if (!row) return;
    const expanded = row.dataset.expanded !== 'true';
    row.dataset.expanded = String(expanded);
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = expanded ? 'Show less' : 'Read more';
  });

  initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
